/**
 * Detalle de niño (sección 4): tabs Datos (solo lectura) · Evaluaciones (sus
 * baterías) · Vínculos activos. El niño se toma del listado global de niños y
 * sus baterías del listado global de baterías (cruce cliente, filtrado por
 * childId). Solo lectura: el admin nunca edita datos clínicos.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { adminApi } from "../../api/endpoints.js";
import { useQuery } from "../../hooks/useQuery.js";
import { EstadoBadge, PerfilBadge, QaTag, edadDesde, fmtFechaCorta } from "./ui.js";
import s from "./admin.module.css";

type Tab = "datos" | "evaluaciones" | "vinculos";

export function AdminChildDetail() {
  const { childId = "" } = useParams();
  const navigate = useNavigate();
  const ninos = useQuery(() => adminApi.ninos());
  const bat = useQuery(() => adminApi.baterias({ limit: 200 }));
  const [tab, setTab] = useState<Tab>("datos");

  const nino = useMemo(
    () => (ninos.data ?? []).find((n) => n.id === childId) ?? null,
    [ninos.data, childId],
  );
  const baterias = useMemo(
    () => (bat.data?.items ?? []).filter((b) => b.childId === childId),
    [bat.data, childId],
  );

  return (
    <>
      <Link to="/admin/ninos" className={s.backLink}>
        ‹ Volver a Niños
      </Link>

      {ninos.loading && !nino ? (
        <div className={s.empty}>Cargando…</div>
      ) : !nino ? (
        <div className={`${s.banner} ${s.bannerError}`}>Niño no encontrado.</div>
      ) : (
        <>
          <div className={s.sectionTitle}>{nino.nombre}</div>
          <div className={s.tabs}>
            <button
              className={`${s.tab} ${tab === "datos" ? s.tabActive : ""}`}
              onClick={() => setTab("datos")}
            >
              Datos
            </button>
            <button
              className={`${s.tab} ${tab === "evaluaciones" ? s.tabActive : ""}`}
              onClick={() => setTab("evaluaciones")}
            >
              Evaluaciones ({baterias.length})
            </button>
            <button
              className={`${s.tab} ${tab === "vinculos" ? s.tabActive : ""}`}
              onClick={() => setTab("vinculos")}
            >
              Vínculos ({nino.terapeutas.length})
            </button>
          </div>

          {tab === "datos" ? (
            <div className={`${s.panel} ${s.panelPad}`}>
              <div className={s.dl}>
                <div>
                  <div className={s.dt}>Nombre</div>
                  <div className={s.dd}>{nino.nombre}</div>
                </div>
                <div>
                  <div className={s.dt}>Fecha de nacimiento</div>
                  <div className={s.dd}>{fmtFechaCorta(nino.fechaNacimiento)}</div>
                </div>
                <div>
                  <div className={s.dt}>Edad</div>
                  <div className={s.dd}>{edadDesde(nino.fechaNacimiento)}</div>
                </div>
                <div>
                  <div className={s.dt}>Padre / madre</div>
                  <div className={s.dd}>
                    {nino.padre ? `${nino.padre.nombre} · ${nino.padre.email}` : "—"}
                  </div>
                </div>
                <div>
                  <div className={s.dt}>ID</div>
                  <div className={`${s.dd} ${s.mono}`}>{nino.id}</div>
                </div>
              </div>
            </div>
          ) : null}

          {tab === "evaluaciones" ? (
            <div className={s.panel}>
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>Banda</th>
                      <th>Estado</th>
                      <th>Perfil</th>
                      <th>Creada</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {baterias.map((b) => (
                      <tr
                        key={b.id}
                        className={s.rowClickable}
                        onClick={() => navigate(`/admin/evaluaciones/${b.id}`)}
                      >
                        <td>
                          {b.banda} {b.modoPrueba ? <QaTag /> : null}
                        </td>
                        <td>
                          <EstadoBadge estado={b.estado} />
                        </td>
                        <td>
                          <PerfilBadge profile={b.profile} />
                        </td>
                        <td className={s.muted}>{fmtFechaCorta(b.createdAt)}</td>
                        <td className={s.muted}>›</td>
                      </tr>
                    ))}
                    {baterias.length === 0 ? (
                      <tr>
                        <td colSpan={5} className={s.empty}>
                          Sin baterías.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {tab === "vinculos" ? (
            <div className={s.panel}>
              <div className={s.tableWrap}>
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th>Terapeuta</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nino.terapeutas.map((t) => (
                      <tr key={t.id}>
                        <td>{t.nombre}</td>
                        <td className={s.muted}>{t.email}</td>
                      </tr>
                    ))}
                    {nino.terapeutas.length === 0 ? (
                      <tr>
                        <td colSpan={2} className={s.empty}>
                          Sin vínculos activos.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
